'use client';

import {
  Button,
  cn,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import {
  getCameraStreamErrorMessage,
  getStoredVideoInputDeviceId,
  listVideoInputDevices,
  pickPreferredVideoInputDevice,
  resolveQrScannerCameraConfig,
  storeVideoInputDeviceId,
  type VideoInputDevice,
} from '@/core/utils/get-camera-media-stream';
import {
  isProductHash,
  parseScannedProductValue,
} from '@/core/utils/product-link.utils';

const SCAN_CONTAINER_ID = 'product-scan-modal-container';

/** Scoped styles so html5-qrcode's injected video/canvas stay inside our square and don't overflow */
const scanAreaStyles = `
  #${SCAN_CONTAINER_ID} {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }
  #${SCAN_CONTAINER_ID} > div {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }
  #${SCAN_CONTAINER_ID} video {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
  }
  #${SCAN_CONTAINER_ID} [id^="qr-shaded-region"],
  #${SCAN_CONTAINER_ID} [id*="qr-shaded"] {
    max-width: 100% !important;
    max-height: 100% !important;
    box-sizing: border-box !important;
  }
`;

/** A camera that failed to start vs. a scanned code that is not a product. */
type ScanError = { kind: 'camera' | 'code'; message: string };

export type ProductScanResult = { hash: string; productType?: string };

export interface ProductScanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResult: (result: ProductScanResult) => void;
  title?: string;
  description?: string;
  cancelLabel?: string;
  cameraSelectLabel?: string;
  cameraErrorLabel?: string;
  cameraErrorHint?: string;
  cameraErrorInsecureLabel?: string;
  cameraErrorPermissionLabel?: string;
  /** Shown when the device has no camera (e.g. a desktop without webcam). */
  cameraErrorNotFoundLabel?: string;
  cameraLoadingLabel?: string;
  emptyCodeLabel?: string;
  invalidCodeLabel?: string;
  retryLabel?: string;
  /** Offered when scanning fails (e.g. no camera): type the code (DEV-553). */
  enterManuallyLabel?: string;
  onEnterManually?: () => void;
}

export function ProductScanModal({
  open,
  onOpenChange,
  onResult,
  title = 'Scan QR code',
  description = "Point your camera at the product's QR code",
  cancelLabel = 'Cancel',
  cameraSelectLabel = 'Camera',
  cameraErrorLabel = 'Camera unavailable',
  cameraErrorHint = 'Allow camera access and try again.',
  cameraErrorInsecureLabel = 'Live camera preview needs a secure connection.',
  cameraErrorPermissionLabel = 'Camera access was denied. Allow camera access in Settings and try again.',
  cameraErrorNotFoundLabel = 'No camera was found on this device.',
  cameraLoadingLabel = 'Starting camera…',
  emptyCodeLabel = 'No product code found in QR code.',
  invalidCodeLabel = 'This does not look like a valid up2promo product code.',
  retryLabel = 'Try again',
  enterManuallyLabel = 'Enter code manually',
  onEnterManually,
}: ProductScanModalProps) {
  const scannerRef = useRef<{ stop: () => Promise<void> } | null>(null);
  const [scanError, setScanError] = useState<ScanError | null>(null);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [scanAttempt, setScanAttempt] = useState(0);
  const [videoDevices, setVideoDevices] = useState<VideoInputDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [devicesReady, setDevicesReady] = useState(false);
  const allowCameraSelection = !isCapacitorNative();

  const stopScanner = useCallback(() => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      scanner.stop().catch(() => {});
    }
  }, []);

  const handleClose = useCallback(() => {
    stopScanner();
    setScanError(null);
    setIsCameraLoading(false);
    setScanAttempt(0);
    setVideoDevices([]);
    setSelectedDeviceId(null);
    setDevicesReady(false);
    onOpenChange(false);
  }, [onOpenChange, stopScanner]);

  const handleRetryScan = useCallback(() => {
    stopScanner();
    setScanError(null);
    setDevicesReady(false);
    setScanAttempt((attempt) => attempt + 1);
  }, [stopScanner]);

  const handleCameraChange = useCallback(
    (deviceId: string) => {
      if (deviceId === selectedDeviceId) return;
      storeVideoInputDeviceId(deviceId);
      stopScanner();
      setScanError(null);
      setSelectedDeviceId(deviceId);
    },
    [selectedDeviceId, stopScanner],
  );

  const [prevOpen, setPrevOpen] = useState(open);
  if (!open && open !== prevOpen) {
    setPrevOpen(open);
    setScanError(null);
    setIsCameraLoading(false);
    setVideoDevices([]);
    setSelectedDeviceId(null);
    setDevicesReady(false);
  } else if (open && open !== prevOpen) {
    setPrevOpen(open);
  }

  if (open && !allowCameraSelection && !devicesReady) {
    setDevicesReady(true);
  }

  useEffect(() => {
    if (!open) {
      stopScanner();
      return;
    }

    if (!allowCameraSelection) {
      return;
    }

    let cancelled = false;

    void (async () => {
      setDevicesReady(false);
      setScanError(null);
      try {
        const devices = await listVideoInputDevices();
        if (cancelled) return;

        setVideoDevices(devices);
        const defaultDeviceId = pickPreferredVideoInputDevice(
          devices,
          getStoredVideoInputDeviceId(),
        );
        setSelectedDeviceId(defaultDeviceId);
        setDevicesReady(true);
      } catch (err: unknown) {
        if (!cancelled) {
          setDevicesReady(true);
          setScanError({
            kind: 'camera',
            message: getCameraStreamErrorMessage(err, {
              default: cameraErrorLabel,
              insecureContext: cameraErrorInsecureLabel,
              permissionDenied: cameraErrorPermissionLabel,
              notFound: cameraErrorNotFoundLabel,
            }),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    scanAttempt,
    allowCameraSelection,
    stopScanner,
    cameraErrorLabel,
    cameraErrorInsecureLabel,
    cameraErrorPermissionLabel,
    cameraErrorNotFoundLabel,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!devicesReady) return;
    if (allowCameraSelection && !selectedDeviceId) return;

    let cancelled = false;
    const containerId = SCAN_CONTAINER_ID;

    void (async () => {
      setScanError(null);
      setIsCameraLoading(true);
      const startDelayMs = isCapacitorNative() ? 0 : 200;
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, startDelayMs);
      });
      if (cancelled) return;

      const el = document.getElementById(containerId);
      if (!el) return;

      try {
        const cameraConfig = allowCameraSelection
          ? selectedDeviceId!
          : await resolveQrScannerCameraConfig();
        if (cancelled) return;

        const { Html5Qrcode } = await import('html5-qrcode');
        const scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;

        await scanner.start(
          cameraConfig,
          {
            fps: 10,
            aspectRatio: 1,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const size = Math.floor(
                Math.min(viewfinderWidth, viewfinderHeight) * 0.85,
              );
              return { width: size, height: size };
            },
          },
          (decodedText) => {
            const parsed = parseScannedProductValue(decodedText);
            if (!parsed.hash.trim()) {
              scannerRef.current = null;
              scanner.stop().catch(() => {});
              setIsCameraLoading(false);
              setScanError({ kind: 'code', message: emptyCodeLabel });
              return;
            }
            if (!isProductHash(parsed.hash)) {
              scannerRef.current = null;
              scanner.stop().catch(() => {});
              setIsCameraLoading(false);
              setScanError({ kind: 'code', message: invalidCodeLabel });
              return;
            }
            scannerRef.current = null;
            scanner.stop().catch(() => {});
            onResult(parsed);
            onOpenChange(false);
          },
          () => {
            // Ignore scan failure (no code in frame)
          },
        );
        if (!cancelled) {
          setIsCameraLoading(false);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setIsCameraLoading(false);
          setScanError({
            kind: 'camera',
            message: getCameraStreamErrorMessage(err, {
              default: cameraErrorLabel,
              insecureContext: cameraErrorInsecureLabel,
              permissionDenied: cameraErrorPermissionLabel,
              notFound: cameraErrorNotFoundLabel,
            }),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [
    open,
    devicesReady,
    selectedDeviceId,
    allowCameraSelection,
    onResult,
    onOpenChange,
    stopScanner,
    cameraErrorLabel,
    cameraErrorInsecureLabel,
    cameraErrorPermissionLabel,
    cameraErrorNotFoundLabel,
    emptyCodeLabel,
    invalidCodeLabel,
  ]);

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
        else onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="flex max-h-[90vh] w-full flex-col gap-4 p-0 sm:max-w-md">
        <ResponsiveDialogHeader className="space-y-1 px-6 pt-6">
          <ResponsiveDialogTitle className="text-lg font-semibold">
            {title}
          </ResponsiveDialogTitle>
          <p className="text-sm text-muted-foreground">{description}</p>
        </ResponsiveDialogHeader>

        <style dangerouslySetInnerHTML={{ __html: scanAreaStyles }} />
        <div className="flex flex-col gap-4 px-6">
          {allowCameraSelection && videoDevices.length > 1 ? (
            <div className="space-y-2">
              <Label htmlFor="product-scan-camera-select">
                {cameraSelectLabel}
              </Label>
              <Select
                value={selectedDeviceId ?? undefined}
                onValueChange={handleCameraChange}
                disabled={isCameraLoading && !scanError}
              >
                <SelectTrigger id="product-scan-camera-select">
                  <SelectValue placeholder={cameraSelectLabel} />
                </SelectTrigger>
                <SelectContent>
                  {videoDevices.map((device) => (
                    <SelectItem key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="relative mx-auto aspect-square w-full max-w-[min(100%,min(70vmin,320px))] overflow-hidden rounded-lg bg-muted">
            <div
              id={SCAN_CONTAINER_ID}
              className={cn(
                'absolute inset-0 size-full transition-opacity duration-200',
                isCameraLoading && !scanError ? 'opacity-0' : 'opacity-100',
              )}
            />
            {isCameraLoading && !scanError ? (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 p-4"
                aria-live="polite"
                aria-busy="true"
              >
                <Skeleton className="absolute inset-0 rounded-lg" />
                <Loader2
                  className="relative z-10 h-8 w-8 animate-spin text-primary"
                  aria-hidden
                />
                <p className="relative z-10 text-center text-sm text-muted-foreground">
                  {cameraLoadingLabel}
                </p>
              </div>
            ) : null}
            {scanError && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-background/90 p-4 text-center"
                role="alert"
              >
                {scanError.kind === 'camera' ? (
                  <>
                    <p className="text-sm font-medium text-destructive">
                      {cameraErrorLabel}
                    </p>
                    <p className="text-xs text-balance break-words text-muted-foreground">
                      {scanError.message === cameraErrorLabel
                        ? cameraErrorHint
                        : scanError.message}
                    </p>
                  </>
                ) : (
                  <p className="text-sm font-medium text-balance break-words text-destructive">
                    {scanError.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <ResponsiveDialogFooter
          className={cn(
            'gap-2 border-t px-6 py-4',
            // Three actions don't fit side by side in the dialog: stack them.
            scanError && onEnterManually && 'flex-col sm:flex-col',
          )}
        >
          {scanError && onEnterManually ? (
            <Button type="button" className="w-full" onClick={onEnterManually}>
              {enterManuallyLabel}
            </Button>
          ) : null}
          {scanError ? (
            <Button
              type="button"
              variant={onEnterManually ? 'outline' : 'primary'}
              className="w-full"
              onClick={handleRetryScan}
            >
              {retryLabel}
            </Button>
          ) : null}
          <Button
            type="button"
            variant={scanError && onEnterManually ? 'ghost' : 'outline'}
            className={cn(scanError && onEnterManually && 'w-full')}
            onClick={handleClose}
          >
            {cancelLabel}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
